<template>
  <div class="fc-view__edit-series">
    <div class="edit-series__form-wrapper">
      <template v-if="isLoading">
        <div class="edit-series__loading-spinner">
          <mds-loader></mds-loader>
        </div>
      </template>
      <template v-else>
        <div class="edit-series__header">
          <label for="create-series-field">
            Enter a series name by combinning both the strategy name and the share class.
            <br />
            The strategy name for this fund is {{initialStrategyName}}
          </label>
        </div>
        <div class="edit-series__new-series">
          <mds-form class="edit-series__form" @submit.native.prevent="handleEditSeriesSubmit">
            <div class="edit-series-body">
              <mds-input
                :id="seriesFields.seriesName.id"
                :placeholder="seriesFields.seriesName.placeholder"
                :label="seriesFields.seriesName.label"
                :error="seriesFields.seriesName.error"
                :error-text="[seriesFields.seriesName.error && seriesFields.seriesName.errorText]"
                v-model.trim="seriesName"
                max-length="255"
                required
              ></mds-input>
            </div>
            <div class="edit-series-footer">
              <mds-button
                class="edit-series-button__continue"
                variation="primary"
                type="submit"
                :disabled="!isValidSeriesData"
              >
                Continue
              </mds-button>
            </div>
          </mds-form>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { get, clone } from 'lodash';
import MdsInput from '@mds/input';
import MdsForm from '@mds/form';

import { mapActions, mapState } from 'vuex';
import * as TargetSeries from '@/services/TargetSeries';

export default {
  name: 'edit-series-name',
  components: {
    MdsForm,
    MdsInput,
  },
  props: {
    componentData: {
      required: true,
      type: Object,
    },
  },
  data() {
    return {
      seriesFields: {
        seriesName: {
          id: 'seriesName',
          label: 'Series Name',
          placeholder: 'Series Name',
          error: false,
          errorText: '',
        },
      },
      seriesName: '',
      shareClass: '',
      initialStrategyName: '',
    };
  },
  created() {
    this.setInitialData();
    const selectedClientId = get(this.selectedClient, 'id');
    const selectedFundSecId = get(this.selectedFund, 'secID');
    if (selectedClientId && selectedFundSecId) {
      this.setIsLoading(true);
      TargetSeries.getStrategyName(selectedClientId, selectedFundSecId)
        .then((seriesData) => {
          this.initialStrategyName = get(seriesData, 'strategyName', '') || '';
        })
        .catch((error) => {
          this.logError({
            data: {
              selectedClient: this.selectedClient,
              selectedFund: this.selectedFund,
            },
            errorFrom: 'EditSeriesName:lifecycle:created',
            details: {
              ...Object.getOwnPropertyDescriptors(error),
              message: error.message,
            },
          });
        })
        .finally(() => {
          this.setIsLoading(false);
        });
    }
  },
  methods: {
    ...mapActions([
      'logError',
      'setIsLoading',
    ]),
    setInitialData() {
      this.seriesName = clone(this.selectedSeries.seriesName);
    },
    handleEditSeriesSubmit() {
      if (!this.isValidSeriesName()) {
        this.seriesFields.seriesName.error = true;
        return false;
      }
      try {
        TargetSeries.editSeriesNameForFiduciaryClient(this.selectedClient?.id, this.seriesName, this.selectedSeries?.id);
      } catch (err) {
        // The api call should be done in background.
      }
      this.seriesFields.seriesName.error = false;
      this.$emit('component-action', {
        name: 'edited-series-name-pending',
        editedSeries: {
          ...this.selectedSeries,
          initialSeriesName: this.selectedSeries.initialSeriesName
            || this.selectedSeries.seriesName,
          seriesName: this.seriesName,
          isNew: false,
          isEdited: true,
          isValid: true,
        },
      });
      return true;
    },
    isValidSeriesName() {
      const charactersLimit = 255;
      if (!this.seriesName) {
        this.seriesFields.seriesName.errorText = 'Enter a series name.';
      } else if (this.seriesName.length > charactersLimit) {
        this.seriesFields.seriesName.errorText = 'The Series Name should not have more than 255 characters.';
      } else {
        this.seriesFields.seriesName.errorText = null;
      }

      if (this.seriesFields.seriesName.errorText) {
        return false;
      }
      return true;
    },
    validateEntries() {
      if (this.isValidSeriesName()) {
        this.seriesFields.seriesName.error = false;
        return true;
      }
      this.seriesFields.seriesName.error = true;
      return false;
    },
  },
  computed: {
    ...mapState({
      isLoading: (state) => state.system.isLoading,
    }),
    selectedFund() {
      return get(this.componentData, 'selectedFund', {});
    },
    selectedClient() {
      return get(this.componentData, 'selectedClient', {});
    },
    selectedSeries() {
      return get(this.componentData, 'selectedSeries', {});
    },
    hasSelectedSeries() {
      return !!this.selectedSeries;
    },
    isValidSeriesData() {
      return !!this.seriesName && !this.seriesFields.seriesName.error;
    },
  },
  watch: {
    seriesName() {
      this.validateEntries();
    },
  },
};
</script>

<style lang="scss">
.fc-view__edit-series {
  margin: calc(#{$mds-space-5-x} * 2);
}

.fc-button__edit-series {
  box-shadow: none;
}

.edit-series__form-wrapper {
  height: 170px;
}

.edit-series__selected-fund {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: $mds-space-4-x;
}

.edit-series__edit-series {
  display: flex;
  flex-direction: column;
}

.edit-series__header {
  label {
    @include mds-level-4-heading();
  }
}

.edit-series-body {
  display: flex;
  justify-content: space-between;
  margin-top: $mds-space-2-and-a-half-x;
  min-height: $fc-main-intr-process-small-forms-min-heigh;
  width: 50%;
  label{
    flex: 1;
    width: 50%;
  }
}
.edit-series-footer {
  margin-top: $fc-int-process-margin-box;
  display: flex;
  justify-content: flex-end;

  .edit-series-button__continue {
    width: 150px;
  }
}
</style>
